import { resizeBase64Image } from '@/utils';
import {
  Box,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
} from '@mui/material';
import { captureException } from '@sentry/react';
import { Close as CloseIcon, Upload as UploadIcon } from 'mdi-material-ui';
import { enqueueSnackbar } from 'notistack';
import { useController } from 'react-hook-form';
import { useFilePicker } from 'use-file-picker';
import { FileSizeValidator } from 'use-file-picker/validators';

const errorLookup = {
  FILE_SIZE_TOO_LARGE: 'File size is too large',
  TOTAL_SIZE_EXCEEDED: 'Total size of all images exceeds 5MB limit',
};

// Calculate the byte size of a base64 data URL string
function getBase64Size(dataUrl) {
  if (!dataUrl) return 0;
  // Remove the data URL prefix (e.g., "data:image/jpeg;base64,")
  const base64String = dataUrl.split(',')[1];
  if (!base64String) return 0;
  // Calculate the actual byte size from base64 (each base64 char represents 6 bits)
  return Math.ceil((base64String.length * 3) / 4);
}

// Calculate total size of all images in bytes
function getTotalImageSize(images = []) {
  return images.reduce((total, img) => total + getBase64Size(img), 0);
}

export function ImagesUploadController({ name, control, disabled, alt, sx }) {
  const {
    field: { value, onChange },
  } = useController({
    name,
    control,
  });

  function handleFilesRejected({ errors }) {
    captureException(
      new Error(errorLookup[errors[0].reason] ?? errors[0].reason),
    );
    enqueueSnackbar(errorLookup[errors[0].reason] ?? errors[0].reason, {
      variant: 'error',
    });
  }

  function handleFilesSuccessfullySelected({ filesContent }) {
    resizeBase64Image(filesContent[0].content, 640, 480).then((dataUrl) => {
      const currentTotalSize = getTotalImageSize(value);
      const newImageSize = getBase64Size(dataUrl);
      const newTotalSize = currentTotalSize + newImageSize;
      const maxTotalSize = 1024 * 1024 * 5;

      if (newTotalSize > maxTotalSize) {
        captureException(new Error('Total image size would exceed 5MB limit'));
        enqueueSnackbar(errorLookup.TOTAL_SIZE_EXCEEDED, {
          variant: 'error',
        });
        return;
      }

      onChange([...(value ?? []), dataUrl]);
    });
  }

  const { openFilePicker, loading } = useFilePicker({
    readAs: 'DataURL',
    accept: 'image/*',
    multiple: false,
    validators: [
      // new FileAmountLimitValidator({ max: 1 }),
      // new FileTypeValidator(['jpg', 'png']),
      new FileSizeValidator({ maxFileSize: 1024 * 1024 * 5 }),
      // new ImageDimensionsValidator({
      //   maxHeight: 900, // in pixels
      //   maxWidth: 1600,
      //   minHeight: 600,
      //   minWidth: 768,
      // }),
    ],
    onFilesRejected: handleFilesRejected,
    onFilesSuccessfullySelected: handleFilesSuccessfullySelected,
  });

  function handleUploadClick() {
    if (!disabled) {
      openFilePicker();
    }
  }

  const totalImageSize = getTotalImageSize(value);
  const warningThreshold = 1024 * 1024 * 4;
  const isNearLimit = totalImageSize >= warningThreshold;
  const uploadButtonDisabled = disabled || isNearLimit;

  return (
    <Stack direction="row" spacing={1} sx={{ overflowX: 'auto' }}>
      {value?.map((src, index) => (
        <Box
          key={index}
          sx={{
            position: 'relative',
            width: 240,
            height: 240,
            borderRadius: 1,
            overflow: 'hidden',
            flexShrink: 0,
          }}
        >
          <img
            src={src}
            alt={`${alt} ${index + 1}`}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
          <Tooltip title="Remove image" arrow>
            <IconButton
              size="small"
              color="error"
              onClick={() => onChange(value.filter((_, i) => i !== index))}
              sx={{
                position: 'absolute',
                top: 4,
                right: 4,
                bgcolor: 'rgba(255,255,255,0.7)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
              }}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
        </Box>
      ))}
      <Tooltip
        title={
          isNearLimit
            ? 'Upload disabled - approaching 5MB limit'
            : 'Upload image'
        }
        arrow
      >
        <Box
          onClick={handleUploadClick}
          sx={{
            display: 'flex',
            position: 'relative',
            overflow: 'hidden',
            width: 1,
            height: 240,
            minWidth: 120,
            bgcolor: 'background.paper',
            borderStyle: 'solid',
            borderColor: isNearLimit ? 'warning.main' : 'divider',
            borderWidth: 1,
            borderRadius: 1,
            opacity: uploadButtonDisabled ? 0.5 : 1,
            cursor: uploadButtonDisabled ? 'not-allowed' : 'pointer',
            '&:hover': {
              borderColor: uploadButtonDisabled
                ? 'warning.main'
                : 'action.active',
              bgcolor: uploadButtonDisabled ? 'action.hover' : 'action.hover',
            },
            ...sx,
          }}
        >
          <UploadIcon
            color={isNearLimit ? 'warning' : 'action'}
            sx={{
              fontSize: 32,
              alignSelf: 'center',
              textAlign: 'center',
              flex: 1,
            }}
          />
          {loading && (
            <CircularProgress
              style={{ position: 'absolute', top: 8, right: 8 }}
              size={16}
              color="primary"
            />
          )}
        </Box>
      </Tooltip>
    </Stack>
  );
}
